import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchCountries } from '../api/countries.js';

export const GeoAutocomplete = ({ value, onChange, disabled }) => {
    const [query, setQuery] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const debounceRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const doSearch = useCallback(async (q) => {
        if (!q || q.length < 1) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        try {
            const results = await searchCountries(q);
            setSuggestions(results);
            setShowDropdown(results.length > 0);
            setHighlightIdx(-1);
        } catch {
            setSuggestions([]);
        }
    }, []);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        onChange(val);
        setSelectedCountry(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(val), 300);
    };

    const handleSelect = (country) => {
        const label = country.nameRu || country.name;
        setQuery(label);
        onChange(label);
        setSelectedCountry(country);
        setShowDropdown(false);
    };

    const handleKeyDown = (e) => {
        if (!showDropdown || suggestions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIdx(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIdx(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && highlightIdx >= 0) {
            e.preventDefault();
            handleSelect(suggestions[highlightIdx]);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    const isBanned = selectedCountry?.isBanned === true;

    return (
        <div className='form-group geo-autocomplete' ref={containerRef}>
            <label className='form-label'>ГЕО</label>
            <input
                className={`form-input ${isBanned ? 'form-input--error' : ''}`}
                type='text'
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
                disabled={disabled}
                placeholder='Страна блогера...'
                autoComplete='off'
            />

            {isBanned && (
                <div className='geo-autocomplete__banned-banner'>
                    ⚠ Страна заблокирована — работа с блогерами из этой страны запрещена
                </div>
            )}

            {showDropdown && suggestions.length > 0 && (
                <div className='geo-autocomplete__dropdown'>
                    {suggestions.map((c, idx) => (
                        <div
                            key={c.code}
                            className={`geo-autocomplete__option ${idx === highlightIdx ? 'highlighted' : ''} ${c.isBanned ? 'banned' : ''}`}
                            onClick={() => handleSelect(c)}
                            onMouseEnter={() => setHighlightIdx(idx)}
                        >
                            <span className='geo-autocomplete__option-name'>
                                {c.nameRu || c.name}
                            </span>
                            <span className='geo-autocomplete__option-code'>{c.code}</span>
                            {c.isBanned && <span className='geo-autocomplete__option-banned'>заблок.</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
